import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthReturnUrlService } from '../services/auth-return-url.service';
import { TranslateService } from '@ngx-translate/core';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { normalizeBackendMessageKey } from '../utils/http-error.utils';
import { PERMISSION_LABEL_KEYS } from '../utils/notification-labels.utils';
import { MFA_REQUIRED_NOTICE_KEY, PASSWORD_CHANGED_NOTICE_KEY, SESSION_REVOKED_NOTICE_KEY } from '../constants/app.constants';

const HANDLED_ERROR_FLAG = '__handledByGlobalErrorHandler';

const LOGIN_REDIRECT_ERROR_KEYS = new Set([
  'ERRORS.ACCOUNT_DEACTIVATED_BY_ADMIN',
  'ERRORS.ACCOUNT_PAUSED_RELOGIN',
  'ERRORS.COMPANY_SUSPENDED',
]);

function getActionPermissionLabelKey(req: HttpRequest<any>, missingPermission: string): string | null {
  const method = req.method.toUpperCase();
  const url = req.url || '';

  if ((method === 'POST' || method === 'PUT' || method === 'DELETE')
    && /\/auth\/properties\/[^/]+\/emergency-instructions(\/|$)/.test(url)) {
    return 'PERMISSIONS.COMPANY_EMERGENCY_INSTRUCTIONS_WRITE';
  }
  if (method === 'DELETE' && /\/auth\/announcements\/[^/]+(\/|$)/.test(url)) {
    return 'PERMISSIONS.ACTION_DELETE_ANNOUNCEMENTS';
  }
  if (method === 'POST' && /\/auth\/(employees|services|caretakers)(\/|$)/.test(url)) {
    return 'PERMISSIONS.ACTION_ADD_CONTACTS';
  }
  if (method === 'PUT' && /\/auth\/(employees|services|caretakers)(\/|$)/.test(url)) {
    return 'PERMISSIONS.ACTION_EDIT_CONTACTS';
  }
  if (method === 'POST' && /\/auth\/create(\/|$)/.test(url)) {
    return 'PERMISSIONS.ACTION_ADD_PROPERTY';
  }
  if (method === 'PUT' && /\/auth\/properties\//.test(url)) {
    return 'PERMISSIONS.ACTION_EDIT_PROPERTY';
  }
  if (method === 'DELETE' && /\/auth\/properties\//.test(url)) {
    return 'PERMISSIONS.ACTION_DELETE_PROPERTY';
  }

  return PERMISSION_LABEL_KEYS[missingPermission] || null;
}

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> => {
  const message = inject(NzMessageService);
  const translate = inject(TranslateService);
  const router = inject(Router);
  const returnUrl = inject(AuthReturnUrlService);

  // Get the auth token from localStorage or sessionStorage
  const authToken = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

  // Clone the request and add the authorization header if the token exists
  const authReq = authToken ? req.clone({ headers: req.headers.set('Authorization', `Bearer ${authToken}`) }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const payload: any = (typeof error.error === 'object' && error.error) || {};
      const messageKey = normalizeBackendMessageKey(payload?.messageKey || payload?.message_key || null);
      const code = payload?.code || null;
      const isLoginRequest = /\/login(\?|$|\/)/.test(req.url || '');

      if (messageKey === 'ERRORS.MISSING_PERMISSION' && typeof payload?.missingPermission === 'string') {
        const permission = payload.missingPermission;
        const labelKey = getActionPermissionLabelKey(req, permission);
        const friendlyPermission = labelKey ? translate.instant(labelKey) : permission;
        message.error(translate.instant('ERRORS.MISSING_PERMISSION', { permission: friendlyPermission }));
        payload[HANDLED_ERROR_FLAG] = true;
        return throwError(() => error);
      }

      if (code === 'SEAT_LIMIT_REACHED' || messageKey === 'ERRORS.SEAT_LIMIT_REACHED_CONTACT') {
        message.error(translate.instant('ERRORS.SEAT_LIMIT_REACHED_CONTACT'));
        payload[HANDLED_ERROR_FLAG] = true;
        return throwError(() => error);
      }

      if (code === 'SESSION_REVOKED') {
        if (!isLoginRequest && sessionStorage.getItem(PASSWORD_CHANGED_NOTICE_KEY) !== '1') {
          sessionStorage.setItem(SESSION_REVOKED_NOTICE_KEY, '1');
        }
        localStorage.removeItem('authToken');
        localStorage.removeItem('appMode');
        sessionStorage.removeItem('authToken');
        payload[HANDLED_ERROR_FLAG] = true;
        if (!isLoginRequest) {
          if (!returnUrl.isAuthEntryUrl(router.url)) {
            router.navigateByUrl(returnUrl.loginUrlTree(router.url));
          }
        }
        return throwError(() => error);
      }

      if (code === 'MFA_REQUIRED') {
        if (!isLoginRequest) sessionStorage.setItem(MFA_REQUIRED_NOTICE_KEY, '1');
        localStorage.removeItem('authToken');
        localStorage.removeItem('appMode');
        sessionStorage.removeItem('authToken');
        payload[HANDLED_ERROR_FLAG] = true;
        if (!isLoginRequest && !returnUrl.isAuthEntryUrl(router.url)) {
          router.navigateByUrl(returnUrl.loginUrlTree(router.url));
        }
        return throwError(() => error);
      }

      if (messageKey === 'ERRORS.NOT_AUTHORIZED' || code === 'FORBIDDEN') {
        message.error(translate.instant('ERRORS.NOT_AUTHORIZED'));
        payload[HANDLED_ERROR_FLAG] = true;
        return throwError(() => error);
      }

      if (messageKey && LOGIN_REDIRECT_ERROR_KEYS.has(messageKey)) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('appMode');
        sessionStorage.removeItem('authToken');
        if (!isLoginRequest) {
          message.error(translate.instant(messageKey));
        }
        payload[HANDLED_ERROR_FLAG] = true;
        if (!isLoginRequest && !returnUrl.isAuthEntryUrl(router.url)) {
          router.navigateByUrl(returnUrl.loginUrlTree(router.url));
        }
        return throwError(() => error);
      }

      if (messageKey) {
        message.error(translate.instant(messageKey));
        payload[HANDLED_ERROR_FLAG] = true;
        return throwError(() => error);
      }

      return throwError(() => error);
    })
  );
};
